import React, { useState, useEffect, useRef } from 'react';

/**
 * COMPONENTE VISUAL: CARDS OVERLAY (HIJO)
 * ------------------------------------------------------------------
 * Renderiza el nuevo diseño vertical, el canvas de lluvia de tarjetas,
 * el destello (flash) y el temblor (shake) de manera optimizada.
 * ------------------------------------------------------------------
 */
const Cards = ({ data }) => {
    // --- MÁQUINA DE ESTADOS DE LA ANIMACIÓN ---
    // step 0: Oculto | step 1: Lanzamiento/Flash | step 2: Tarjeta Grande + Lluvia
    const [animStep, setAnimStep] = useState(0); 
    const [cardPayload, setCardPayload] = useState(null);
    const [isBoardVisible, setIsBoardVisible] = useState(true);

    const canvasRef = useRef(null);
    const requestRef = useRef(null);
    const particlesRef = useRef([]);
    const sequenceTimeouts = useRef([]);

    // --- ESCUCHA DEL SOCKET ---
    useEffect(() => {
        if (!data) return;

        const { type, data: payload } = data;

        if (type === 'cards.trigger_overlay') {
            const { action } = payload;

            if (action === 'show_board') { setIsBoardVisible(true); return; }
            if (action === 'hide_board') { setIsBoardVisible(false); return; }
            if (action === 'close') { abortSequence(); return; }

            if (action === 'show_card') {
                setCardPayload(payload);
                startAnimationSequence(payload);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // --- CONTROL DE SECUENCIA DE ANIMACIÓN ---
    const startAnimationSequence = (payload) => {
        abortSequence(); // Limpia secuencias anteriores para permitir reinicio inmediato
        
        // Paso 1: Tarjeta volando + Shake + Flash
        setAnimStep(1);

        // Paso 2: Aparece Tarjeta Principal + Inicia Lluvia
        sequenceTimeouts.current.push(setTimeout(() => {
            setAnimStep(2);
            startRain(payload);
        }, 550));
    };

    const abortSequence = () => {
        sequenceTimeouts.current.forEach(clearTimeout);
        sequenceTimeouts.current = [];
        stopRain();
        setAnimStep(0);
    };

    // --- MOTOR DEL CANVAS (LLUVIA DE TARJETAS) OPTIMIZADO ---
    const startRain = (payload) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        
        // Generamos solo 30 partículas para cuidar los recursos de OBS
        particlesRef.current = Array.from({ length: 30 }, () => createParticle(canvas.width, canvas.height, true));
        
        const color = payload?.cardType === 'yellow' ? '#facc15' : '#ef4444';

        const animate = () => {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            particlesRef.current.forEach(p => {
                p.y += p.speedY;
                p.x += p.speedX;
                p.angle += p.spinZ;

                if (p.y > canvas.height + 100) {
                    Object.assign(p, createParticle(canvas.width, canvas.height, false));
                }

                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.angle);
                ctx.scale(Math.cos(p.y * p.spinX), Math.sin(p.y * p.spinY) > 0 ? 1 : 0.5);

                ctx.fillStyle = color;
                ctx.strokeStyle = 'rgba(255,255,255,0.8)';
                ctx.lineWidth = 1;

                ctx.beginPath();
                ctx.roundRect(-p.width/2, -p.height/2, p.width, p.height, 3);
                ctx.fill();
                ctx.stroke();

                ctx.restore();
            });

            requestRef.current = requestAnimationFrame(animate);
        };
        requestRef.current = requestAnimationFrame(animate);
    };

    const stopRain = () => {
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    };

    const createParticle = (width, height, initial = false) => ({
        x: Math.random() * width,
        y: initial ? Math.random() * -height : -50,
        width: Math.random() * 15 + 15,
        height: (Math.random() * 15 + 15) * 1.4,
        speedY: Math.random() * 3 + 2,
        speedX: (Math.random() - 0.5) * 2,
        angle: Math.random() * Math.PI * 2,
        spinX: Math.random() * 0.1,
        spinY: Math.random() * 0.1,
        spinZ: Math.random() * 0.1
    });

    // --- RENDERIZADO DEL UI ---
    if (!cardPayload) return null;

    const { cardType, animationStyle, zoom = 100, player } = cardPayload;
    
    // Variables dinámicas según el tipo de tarjeta
    const isRed = cardType === 'red';
    const cardTitle = isRed ? "TARJETA ROJA" : "TARJETA AMARILLA";
    const cssModifier = isRed ? "Red" : "Yellow";
    
    // Lógica del logo de marca
    const interaktiaLogo = isRed ? "/img/overlays/cards/interaktia-blanco.png" : "/img/overlays/cards/interaktia.png";
    const playerPhotoPath = player.photoUrl ? `/img/games/cards/players/${player.photoUrl}` : null;

    // Evaluamos si usamos el contenedor general, aplicándole opacidad, zoom y el efecto de temblor (shake)
    const containerClasses = [
        "cards-overlay-wrapper",
        animStep === 1 ? "do-shake" : "",
        !isBoardVisible ? "hidden-board" : ""
    ].join(" ");

    return (
        <div className={containerClasses} style={{ '--zoom-scale': zoom / 100 }}>
            
            {/* CANVAS DE LLUVIA (Se monta sobre toda la pantalla) */}
            <canvas ref={canvasRef} className="card-rain-canvas" />

            {/* DESTELLO EN PANTALLA */}
            <div className={`flash-overlay ${animStep === 1 ? 'do-flash' : ''}`} style={{ backgroundColor: isRed ? '#ef4444' : '#facc15' }}></div>

            {/* TARJETA VOLADORA (Paso 1) */}
            {animStep === 1 && (
                <svg className={`flying-card ${animationStyle}`} viewBox="0 0 100 140" style={{ fill: isRed ? '#ef4444' : '#facc15' }}>
                    <rect x="10" y="10" width="80" height="120" rx="8" stroke="#fff" strokeWidth="3" />
                    <path d="M 20 20 L 40 20" stroke="rgba(0,0,0,0.2)" strokeWidth="4" strokeLinecap="round"/>
                    <path d="M 20 35 L 60 35" stroke="rgba(0,0,0,0.2)" strokeWidth="4" strokeLinecap="round"/>
                </svg>
            )}

            {/* GRÁFICO PRINCIPAL DE LA TARJETA VERTICAL (Paso 2) */}
            <div className={`penalty-card-wrapper ${animStep === 2 ? 'show' : ''}`}>
                <div className={`card__data card__data--${cssModifier}`}>
                    <p className="card__data__TeamName">{cardTitle}</p>

                    {/* RENDERIZADO CONDICIONAL: Foto vs No Foto */}
                    {playerPhotoPath ? (
                        <div>
                            <div className="card__data__Photo" style={{ backgroundImage: `url(${playerPhotoPath})` }}></div>
                            <p className={`card__data__Photo__Number card__data__Photo__Number--${cssModifier}`}>#{player.dorsal}</p>
                        </div>
                    ) : (
                        <p className="card__data__Number">#{player.dorsal}</p>
                    )}

                    <p className="card__data__Name">{player.name}</p>
                    <p className="card__data__TeamName">{player.teamName}</p>
                    
                    <img src={interaktiaLogo} className="card__data__Logo mt-4" alt="Interaktia Logo" />
                </div>
            </div>

        </div>
    );
};

export { Cards };